import { motion } from "framer-motion";
import {
  FileText, IdCard, MapPin, Building, FileSignature, Landmark, CreditCard,
  Camera, ClipboardList, Package, KeyRound, Files,
} from "lucide-react";

const DOCS = [
  { icon: IdCard, label: "PAN" },
  { icon: IdCard, label: "Aadhaar" },
  { icon: MapPin, label: "Address proof" },
  { icon: Building, label: "Business address proof" },
  { icon: FileSignature, label: "Partnership deed" },
  { icon: Landmark, label: "Incorporation documents" },
  { icon: CreditCard, label: "Bank details" },
  { icon: Camera, label: "Photographs" },
  { icon: ClipboardList, label: "Business activity details" },
  { icon: Package, label: "Product information" },
  { icon: KeyRound, label: "Rental / ownership documents" },
  { icon: Files, label: "Other applicable documents" },
];

const STACK_CARDS = [
  { title: "Incorporation Certificate", color: "#FF8800", rot: "-8deg", z: 1 },
  { title: "GST Registration", color: "#00B0FF", rot: "4deg", z: 2 },
  { title: "Udyam / MSME", color: "#00E676", rot: "-3deg", z: 3 },
];

export function Documents() {
  return (
    <section data-testid="documents-section" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,136,0,0.05),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">Document Support</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="documents-headline">
              DOCUMENTS <span className="text-gradient-saffron">MADE SIMPLE</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
              LaunchLab helps entrepreneurs organize the common business documents needed
              across registrations and applications:
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="documents-grid">
              {DOCS.map((d, i) => (
                <motion.div
                  key={d.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="glass rounded-xl px-3.5 py-3 flex items-center gap-2.5 hover:border-[#00B0FF]/40 transition-colors"
                  data-testid={`document-item-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <d.icon className="w-4 h-4 text-[#00B0FF] shrink-0" strokeWidth={1.8} />
                  <span className="text-xs text-slate-200 leading-tight">{d.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Futuristic 3D document stack */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="relative h-[380px] sm:h-[440px] hidden sm:block"
            style={{ perspective: "1200px" }}
            data-testid="documents-3d-stack"
          >
            <div className="absolute inset-0 flex items-center justify-center" style={{ transformStyle: "preserve-3d", transform: "rotateX(12deg) rotateY(-14deg)" }}>
              {STACK_CARDS.map((c, i) => (
                <div
                  key={c.title}
                  className="absolute w-64 sm:w-72 rounded-2xl glass p-6 animate-float"
                  style={{
                    transform: `rotate(${c.rot}) translateZ(${i * 55}px) translateY(${i * -22}px)`,
                    zIndex: c.z,
                    animationDelay: `${i * 0.8}s`,
                    borderColor: `${c.color}44`,
                    boxShadow: `0 0 40px ${c.color}1e, 0 25px 50px rgba(0,0,0,0.5)`,
                  }}
                  data-testid={`documents-stack-card-${i}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <FileText className="w-6 h-6" style={{ color: c.color }} strokeWidth={1.6} />
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ border: `1.5px dashed ${c.color}88` }}
                    >
                      <div className="w-4 h-4 rounded-full" style={{ background: `${c.color}55` }} />
                    </div>
                  </div>
                  <p className="font-display font-semibold text-white text-sm tracking-wide">{c.title}</p>
                  <div className="mt-4 space-y-2">
                    <div className="h-1.5 rounded bg-white/10 w-full" />
                    <div className="h-1.5 rounded bg-white/10 w-4/5" />
                    <div className="h-1.5 rounded bg-white/10 w-3/5" />
                  </div>
                  <p className="mt-4 font-mono-tech text-[8px] tracking-[0.3em] uppercase" style={{ color: c.color }}>
                    Verified · LaunchLab
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
